/* eslint-disable @typescript-eslint/no-unused-vars */
import { gql } from '@apollo/client';

import {
  LESSON_RECORDING_FIELDS,
  ZOOM_RECORDING_FIELDS,
  ZOOM_ACCOUNT_FIELDS,
  ZOOM_AUDIT_ENTRY_FIELDS,
  ZOOM_LESSON_FIELDS,
  GROUP_ZOOM_SCHEDULE_FIELDS,
  ZOOM_PROCESSING_LOG_FIELDS,
  ZOOM_ACCOUNT_USAGE_FIELDS,
} from '../fragments/zoom';

export const ZoomDashboardQuery = gql`
  query ZoomDashboard($dateFrom: Date!, $dateTo: Date!) {
    zoomDashboard(dateFrom: $dateFrom, dateTo: $dateTo) {
      dateFrom
      dateTo
      lessonsTotal
      lessonsMatched
      lessonsMissing
      lessonsSkipped
      recordingsTotal
      recordingsOrphan
      pipeline {
        downloading
        uploaded
        aiProcessing
        aiCompleted
        published
        failed
      }
    }
  }
`;

/**
 * Lesson-centric list: every GroupLesson in a date range (across all groups),
 * with its optional `zoomRecordingMeta`. Backs the «Lessons» list and the
 * «Matching» left panel — see docs/zoom_backend_tz.md for the backend query.
 */
export const AllZoomLessonsQuery = gql`
  ${ZOOM_LESSON_FIELDS}
  query AllZoomLessons(
    $dateFrom: Date!
    $dateTo: Date!
    $groupId: ID
    $courseId: ID
    $status: String
    $first: Int = 50
    $after: String
  ) {
    allZoomLessons(
      dateFrom: $dateFrom
      dateTo: $dateTo
      groupId: $groupId
      courseId: $courseId
      status: $status
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          ...ZoomLessonFields
        }
      }
    }
  }
`;

/**
 * Single lesson for the `/zoom/lessons/:id` card. `zoomLesson` is the
 * ERP-scoped query (Zoom roles) that returns the `GroupLessonForLMSType`
 * directly — keyed by the GroupLesson id, with its optional `zoomRecordingMeta`.
 */
export const LessonRecordingByLessonQuery = gql`
  ${LESSON_RECORDING_FIELDS}
  query LessonRecordingByLesson($groupLessonId: ID!) {
    zoomLesson(groupLessonId: $groupLessonId) {
      ...GroupLessonForZoomFields
      zoomRecordingMeta {
        ...LessonRecordingFields
      }
    }
  }
`;

export const AllZoomRecordingsQuery = gql`
  ${ZOOM_RECORDING_FIELDS}
  query AllZoomRecordings(
    $dateFrom: Date
    $dateTo: Date
    $accountId: ID
    $groupId: ID
    $orphanOnly: Boolean
    $tooShort: Boolean
    $first: Int = 50
    $after: String
  ) {
    allZoomRecordings(
      dateFrom: $dateFrom
      dateTo: $dateTo
      accountId: $accountId
      groupId: $groupId
      orphanOnly: $orphanOnly
      tooShort: $tooShort
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          ...ZoomRecordingFields
        }
      }
    }
  }
`;

/** Zoom storage/usage summary — backs the dashboard storage widget. */
export const ZoomAccountUsageQuery = gql`
  ${ZOOM_ACCOUNT_USAGE_FIELDS}
  query ZoomAccountUsage {
    zoomAccountUsage {
      ...ZoomAccountUsageFields
    }
  }
`;

/** Count of «too short» junk recordings — for the dashboard «needs attention». */
export const ZoomTooShortCountQuery = gql`
  query ZoomTooShortCount {
    allZoomRecordings(tooShort: true, first: 1) {
      totalCount
    }
  }
`;

export const AllZoomAccountsQuery = gql`
  ${ZOOM_ACCOUNT_FIELDS}
  query AllZoomAccounts($isActive: Boolean, $first: Int = 100) {
    allZoomAccounts(isActive: $isActive, first: $first) {
      edges {
        node {
          ...ZoomAccountFields
        }
      }
    }
  }
`;

/**
 * All groups that already have a Zoom account bound — one request backs every
 * account card on `/zoom/accounts` (grouped client-side by `zoomAccount.id`).
 * Relies on the `hasZoomAccount` filter — see docs/zoom_accounts_groups_tz.md.
 */
export const ZoomAssignedGroupsQuery = gql`
  query ZoomAssignedGroups($first: Int = 200) {
    allGroups(hasZoomAccount: true, first: $first) {
      edges {
        node {
          id
          name
          startDate
          zoomAccount {
            id
            displayName
          }
        }
      }
    }
  }
`;

/**
 * Groups with no Zoom account bound — backs the «groups without account» block
 * on `/zoom/accounts`. Searchable via `query`.
 */
export const ZoomUnassignedGroupsQuery = gql`
  query ZoomUnassignedGroups($query: String, $first: Int = 50) {
    allGroups(hasZoomAccount: false, query: $query, first: $first) {
      edges {
        node {
          id
          name
          startDate
        }
      }
    }
  }
`;

/** Every group's Zoom auto-processing schedule — backs the «Schedule» tab. */
export const AllGroupZoomSchedulesQuery = gql`
  ${GROUP_ZOOM_SCHEDULE_FIELDS}
  query AllGroupZoomSchedules($first: Int = 500) {
    allGroupZoomSchedules(first: $first) {
      edges {
        node {
          ...GroupZoomScheduleFields
        }
      }
    }
  }
`;

/**
 * Step-by-step processing journal of one lesson — backs the «Processing log»
 * timeline on the lesson card and the full-log modal (optional step/level
 * filters). Entries come back ordered by `createdAt` ascending.
 */
export const AllZoomProcessingLogQuery = gql`
  ${ZOOM_PROCESSING_LOG_FIELDS}
  query AllZoomProcessingLog(
    $lessonId: ID
    $level: String
    $step: String
    $first: Int = 200
  ) {
    allZoomProcessingLog(
      lessonId: $lessonId
      level: $level
      step: $step
      first: $first
    ) {
      totalCount
      edges {
        node {
          ...ZoomProcessingLogFields
        }
      }
    }
  }
`;

export const ZoomAuditLogQuery = gql`
  ${ZOOM_AUDIT_ENTRY_FIELDS}
  query ZoomAuditLog(
    $action: String
    $userId: ID
    $targetKind: String
    $dateFrom: Date
    $dateTo: Date
    $first: Int = 50
    $after: String
  ) {
    zoomAuditLog(
      action: $action
      userId: $userId
      targetKind: $targetKind
      dateFrom: $dateFrom
      dateTo: $dateTo
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          ...ZoomAuditEntryFields
        }
      }
    }
  }
`;
